import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import SEO from '../components/seo/SEO';
import Footer from '../components/layout/Footer';
import TransitionLink from '../components/layout/TransitionLink';
import OpPageHeader from '../components/optimus/OpPageHeader';
import {
  MicrosoftLogo,
  NvidiaLogo,
  GoogleCloudLogo,
  MsuLogo,
  UsFlag,
  KuwaitFlag,
  CertChip,
} from '../components/optimus/brandLogos';
import { skills } from '../data/skills';

interface AboutPageProps {
  darkMode: boolean;
}

/**
 * About page in the Optimus design system — portrait + bio, real experience
 * and education timeline, the skill matrix from src/data/skills.ts, and
 * certifications.
 */
const AboutPage: React.FC<AboutPageProps> = ({ darkMode }) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const experience = [
    { role: t('op.experience.eZainRole'), org: t('op.experience.eZainOrg'), period: `Aug 2026 – ${t('op.experience.present')}` },
    { role: t('op.experience.e1Role'), org: 'Intgr8AI', period: `May 2025 – ${t('op.experience.present')}` },
    { role: t('op.experience.e2Role'), org: 'Treevah', period: 'Apr 2026 – Jun 2026' },
    { role: t('op.experience.e3Role'), org: "Wyatt's Creative Works", period: 'Nov 2024 – May 2025' },
    { role: t('op.experience.e4Role'), org: t('op.experience.e4Org'), period: 'Nov 2022 – Dec 2024' },
    { role: t('op.experience.e6Role'), org: 'Thabi6ni Co.', period: 'Feb 2019 – Jul 2021' },
  ];

  const education = [
    { degree: t('op.experience.e5Role'), school: t('op.experience.e5Org'), period: '2021 – 2025' },
    { degree: t('about.education'), school: 'AlBayan Bilingual School', period: '2015 – 2021' },
  ];

  const certifications = [
    { logo: <MicrosoftLogo />, label: 'Generative AI' },
    { logo: <NvidiaLogo />, label: 'Deep Learning' },
    { logo: <GoogleCloudLogo />, label: 'GenAI Fundamentals' },
    { logo: <MsuLogo />, label: "Dean's List" },
  ];

  const skillGroups: { title: string; items: string[] }[] = [
    { title: t('op.stack.catAi'), items: skills.ai.map((s) => s.name).concat(['LangChain', 'RAG', 'Vector DBs']) },
    { title: t('op.stack.catWeb'), items: skills.web.map((s) => s.name) },
    { title: t('op.stack.catMobile'), items: skills.mobile.map((s) => s.name) },
    { title: t('op.stack.catDb'), items: skills.databases.map((s) => s.name) },
    { title: t('op.stack.catHardware'), items: skills.hardware.map((s) => s.name).slice(0, 6) },
    { title: t('op.stack.catDevops'), items: skills.devops.map((s) => s.name).concat(['Azure']) },
  ];

  const interests = [t('about.aiResearch'), t('about.iot'), t('about.robotics')];

  return (
    <main className="op-page noise-overlay relative min-h-screen overflow-x-hidden">
      <SEO
        title="About Talal Alkhaled - Computer Engineer"
        description={t('about.description')}
        url="https://talkhaled.com/about"
      />

      <OpPageHeader
        label={t('op.sub.aboutLabel')}
        line1={t('op.sub.aboutH1')}
        line2={t('op.sub.aboutH2')}
      />

      {/* Portrait + bio */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 lg:pb-32">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-20 border-t border-foreground/10 pt-12 lg:pt-16 items-start">
          <div className="lg:col-span-2">
            <div className="border border-foreground/10 overflow-hidden">
              <img
                src="/Talal.webp"
                alt={t('op.sub.photoAlt')}
                className="w-full aspect-square object-cover"
              />
            </div>
            <div className="flex items-center justify-between mt-4 font-mono text-xs text-muted-foreground">
              <span>Talal Alkhaled</span>
              <span dir="ltr">{t('about.location')}</span>
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="text-xl lg:text-2xl text-foreground leading-relaxed mb-8">
              {t('about.asComputerEngineeringStudent')}
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-12">
              {t('about.enjoyTacklingComplexProblems')}
            </p>

            {/* Quick facts */}
            <div className="grid sm:grid-cols-3 gap-px bg-foreground/10 border border-foreground/10 mb-12">
              <div className="bg-background p-5">
                <div className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2">
                  {t('op.sub.institutionLabel')}
                </div>
                <div className="flex items-center gap-2.5 text-sm text-foreground">
                  <MsuLogo className="w-5 h-5 shrink-0" />
                  {t('about.institution')}
                </div>
              </div>
              <div className="bg-background p-5">
                <div className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2">
                  {t('about.languages')}
                </div>
                <div className="text-sm text-foreground space-y-1.5">
                  <div className="flex items-center gap-2.5">
                    <UsFlag className="w-5 h-auto shrink-0" />
                    {t('about.english')}
                  </div>
                  <div className="flex items-center gap-2.5">
                    <KuwaitFlag className="w-5 h-auto shrink-0" />
                    {t('about.arabic')}
                  </div>
                </div>
              </div>
              <div className="bg-background p-5">
                <div className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2">
                  {t('about.interests')}
                </div>
                <div className="text-sm text-foreground">{interests.join(' · ')}</div>
              </div>
            </div>

            {/* Certifications */}
            <span className="font-mono text-xs tracking-widest text-muted-foreground uppercase block mb-4">
              {t('op.experience.certsLabel')}
            </span>
            <div className="flex flex-wrap gap-3">
              {certifications.map((cert) => (
                <CertChip key={cert.label} logo={cert.logo} label={cert.label} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Experience + Education + Skills */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 lg:pb-32">
        {/* min-w-0 keeps the nowrap dates from inflating the mobile grid track */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-3 text-sm font-mono text-muted-foreground mb-8">
              <span className="w-8 h-px bg-foreground/30" />
              {t('op.experience.label')}
            </span>
            <div className="border border-foreground/10">
              {experience.map((entry) => (
                <div
                  key={entry.role}
                  className="px-6 py-5 border-b border-foreground/5 last:border-b-0 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{entry.role}</div>
                    <div className="text-sm text-muted-foreground truncate">{entry.org}</div>
                    <div dir="ltr" className="sm:hidden font-mono text-xs text-muted-foreground mt-1">{entry.period}</div>
                  </div>
                  <span dir="ltr" className="hidden sm:block font-mono text-sm text-muted-foreground shrink-0 whitespace-nowrap">
                    {entry.period}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-12">
              <span className="inline-flex items-center gap-3 text-sm font-mono text-muted-foreground mb-8">
                <span className="w-8 h-px bg-foreground/30" />
                {t('op.sub.educationLabel')}
              </span>
              <div className="border border-foreground/10">
                {education.map((entry) => (
                  <div
                    key={entry.school}
                    className="px-6 py-5 border-b border-foreground/5 last:border-b-0 flex items-center justify-between gap-4"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{entry.degree}</div>
                      <div className="text-sm text-muted-foreground truncate">{entry.school}</div>
                      <div dir="ltr" className="sm:hidden font-mono text-xs text-muted-foreground mt-1">{entry.period}</div>
                    </div>
                    <span dir="ltr" className="hidden sm:block font-mono text-sm text-muted-foreground shrink-0 whitespace-nowrap">
                      {entry.period}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="min-w-0">
            <span className="inline-flex items-center gap-3 text-sm font-mono text-muted-foreground mb-8">
              <span className="w-8 h-px bg-foreground/30" />
              {t('about.technicalSkills')}
            </span>
            <div className="grid sm:grid-cols-2 gap-px bg-foreground/10 border border-foreground/10">
              {skillGroups.map((group) => (
                <div key={group.title} className="bg-background p-5">
                  <div className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-3">
                    {group.title}
                  </div>
                  <div className="text-sm text-foreground leading-relaxed">
                    {group.items.join(' · ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 lg:pb-32">
        <div className="border border-foreground px-8 lg:px-16 py-12 lg:py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <h2 className="text-3xl lg:text-5xl font-display tracking-tight">
            {t('about.letsWorkTogether')}
          </h2>
          <TransitionLink
            to="/contact"
            className="inline-flex items-center justify-center bg-foreground hover:bg-foreground/90 text-background px-8 h-14 text-base rounded-full group font-medium transition-colors shrink-0"
          >
            {t('about.getInTouch')}
            <ArrowRight className={`w-4 h-4 transition-transform ${isArabic ? 'mr-2 rotate-180 group-hover:-translate-x-1' : 'ml-2 group-hover:translate-x-1'}`} />
          </TransitionLink>
        </div>
      </section>

      <Footer darkMode={darkMode} />
    </main>
  );
};

export default AboutPage;
